import React, { createElement, useEffect, useState } from 'react';

export const withWizard = ({
  type = 'INNER WIZARD',
  initialValues = null,
  disabled = false,
  completed = false,
  initialStep = 0,
  steps = [],
  onStepChanged = () => { },
  onValuesChanged = () => { },
  onCompleted = () => { },
  ...props
}) => (WrapperComponent) => {
  const [state, setState] = useState({
    childStep: 0,
    currentStep: initialStep,
    values: initialValues,
    isDisabled: disabled,
    isCompleted: completed,
    previousStep: null,
    stepHistory: [initialStep],
  });

  const {
    isCompleted, isDisabled, currentStep, values, stepHistory,
  } = state;

  useEffect(() => {
    onStepChanged({ currentStep });
  }, [currentStep]);

  useEffect(() => {
    if (isCompleted) {
      onCompleted(values);
    }
  }, [isCompleted, values]);

  useEffect(() => {
    onValuesChanged(state);
  }, [state]);

  const navigateForward = async (stepValues = {}) => {
    const currentStepHasNextStepIndex = !!stepValues?.formValues?.nextStepIndexPosition;
    const nextIndex = currentStepHasNextStepIndex ? stepValues.formValues.nextStepIndexPosition : currentStep + 1;

    if (currentStep === steps.length - 1) {
      if (!isCompleted) {
        setState((prevState) => ({
          ...prevState,
          values: {
            ...prevState.values,
            childStep: stepValues.childStep,
            nextStepIndexPosition: stepValues?.formValues.nextStepIndexPosition,
            formValues: {
              ...prevState.values?.formValues,
              [stepValues.childStep]: stepValues?.formValues,
            },
          },
          isCompleted: true,
        }));
      }
      return;
    }
    setState((prevState) => {
      const shouldAddToHistory = prevState.stepHistory[prevState.stepHistory.length - 1] !== nextIndex;
      const newHistory = shouldAddToHistory ? [...prevState.stepHistory, nextIndex] : prevState.stepHistory;

      return {
        ...prevState,
        previousStep: prevState.currentStep,
        currentStep: nextIndex,
        stepHistory: newHistory,
        values: {
          ...prevState.values,
          childStep: stepValues.childStep,
          nextStepIndexPosition: stepValues?.formValues.nextStepIndexPosition,
          formValues: {
            ...prevState.values?.formValues,
            [stepValues.childStep]: stepValues?.formValues,
          },
        },
      };
    });
  };

  const navigateBack = ({ childStep } = {}) => {
    if (currentStep === 0 || stepHistory.length <= 1) return;

    setState((prevState) => {
      const newHistory = [...prevState.stepHistory];
      newHistory.pop();
      const targetStep = newHistory[newHistory.length - 1];

      const has_child_step = !isNaN(childStep);
      return has_child_step ? {
        ...prevState,
        currentStep: targetStep,
        previousStep: prevState.currentStep,
        stepHistory: newHistory,
        childStep,
      } : {
        ...prevState,
        currentStep: targetStep,
        previousStep: prevState.currentStep,
        stepHistory: newHistory,
      };
    });
  };

  const goToStep = (stepIndex) => {
    if (stepIndex < 0 || stepIndex > steps.length - 1) return;
    setState((prevState) => {
      const shouldAddToHistory = prevState.stepHistory[prevState.stepHistory.length - 1] !== stepIndex;
      const newHistory = shouldAddToHistory ? [...prevState.stepHistory, stepIndex] : prevState.stepHistory;

      return {
        ...prevState,
        previousStep: prevState.currentStep,
        currentStep: stepIndex,
        stepHistory: newHistory,
      };
    });
  };

  const enable = (enabled) => {
    setState((prevState) => ({
      ...prevState,
      isDisabled: !enabled,
    }));
  };

  const reset = () => {
    setState((prevState) => ({
      ...prevState,
      values: initialValues,
      previousStep: null,
      stepHistory: [initialStep],
    }));
  };

  const step = steps[currentStep || 0];
  const { content, ...stepProps } = step;
  return (
    <>
      {createElement(
        WrapperComponent,
        {
          currentStep,
          values,
          disabled: isDisabled,
          completed: isCompleted,
          navigateForward,
          navigateBack,
          reset,
          goToStep,
          enable,
          ...props,
          ...stepProps,
        },
        content
        && createElement(content, {
          currentStep,
          values,
          disabled: isDisabled,
          completed: isCompleted,
          navigateForward,
          navigateBack,
          reset,
          goToStep,
          enable,
          initialStep: state.childStep,
          ...props,
          ...stepProps,
        }),
      )}
      <div id="actions-portal" />
    </>
  );
};
